import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAnalytics } from '@/hooks/useAnalytics';
import { LandingButton } from './LandingButton';
import { DashboardMockup } from './DashboardMockup';

const ArrowRightIcon = ({ size = 16 }: { size?: number }) => (
  <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M5 12h14" />
    <path d="m12 5 7 7-7 7" />
  </svg>
);

export const HeroSection = React.memo(() => {
  const { t } = useTranslation();
  const { track } = useAnalytics();

  return (
    <section className="relative min-h-screen flex flex-col items-center justify-start px-6 py-20 md:py-24">
      <h1
        className="text-4xl md:text-5xl lg:text-6xl font-medium text-center max-w-3xl px-6 leading-tight mb-6"
        style={{
          background: 'linear-gradient(to bottom, #000000, #000000, rgba(0, 0, 0, 0.6))',
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent',
          backgroundClip: 'text',
          letterSpacing: '-0.05em',
        }}
      >
        {t('landing.hero.titleLine1')} <br />
        {t('landing.hero.titleLine2')}
      </h1>
      <p className="text-sm md:text-base text-center max-w-2xl px-6 mb-10 text-gray-500">
        {t('landing.hero.subtitle')}
      </p>
      <div className="flex flex-col sm:flex-row items-center gap-4 relative z-10 mb-4">
        <Link
          to="/auth?tab=signup"
          onClick={() => track('landing_cta_clicked', { location: 'hero' })}
        >
          <LandingButton type="button" variant="gradient" size="lg" className="rounded-lg flex items-center justify-center">
            {t('landing.hero.ctaPrimary')}
            <ArrowRightIcon size={16} />
          </LandingButton>
        </Link>
        <a
          href="#precos"
          className="inline-flex items-center justify-center rounded-lg border border-gray-300 bg-white text-black hover:bg-gray-50 transition px-6 h-12 text-base font-medium"
        >
          {t('landing.hero.ctaSecondary')}
        </a>
      </div>
      <p className="text-xs mb-16 text-gray-400">
        {t('landing.hero.disclaimer')}
      </p>
      <div className="w-full max-w-6xl relative pb-20">
        <div
          className="absolute left-1/2 w-[90%] pointer-events-none z-0"
          style={{ top: '-23%', transform: 'translateX(-50%)', opacity: 0.35 }}
          aria-hidden="true"
        >
          <img src="https://i.postimg.cc/Ss6yShGy/glows.png" alt="" className="w-full h-auto" loading="eager" />
        </div>
        <div className="relative z-10">
          <DashboardMockup />
        </div>
      </div>
    </section>
  );
});
HeroSection.displayName = 'HeroSection';
